import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import ScrollToTop from '../components/ScrollToTop';
import { 
  Wifi, 
  TrendingDown, 
  Users, 
  Zap, 
  Upload, 
  Check, 
  AlertCircle,
  Package,
  Clock,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { trackLead } from '../utils/fbpixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BulkOrders = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    customer_name: '',
    company_name: '',
    email: '',
    phone: '',
    job_title: '',
    product_type: 'NFC Keychains',
    quantity: 50,
    needs_branding: false,
    logo_url: '',
    brand_colors: '#000000',
    custom_text: '',
    design_notes: '',
    use_case: 'Corporate Event',
    delivery_date: '',
    shipping_address: '',
    special_requirements: '',
    budget_range: '',
    timeline_flexibility: 'flexible',
    request_sample: false,
    sample_address: ''
  });
  
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);

  // Calculate live quote when quantity or product type changes
  useEffect(() => {
    if (formData.quantity >= 50) {
      calculateQuote();
    }
  }, [formData.quantity, formData.product_type]);

  const calculateQuote = async () => {
    setLoadingQuote(true);
    try {
      const response = await axios.post(`${API_URL}/api/bulk-orders/calculate-quote`, null, {
        params: {
          product_type: formData.product_type,
          quantity: formData.quantity
        }
      });
      setQuote(response.data);
    } catch (error) {
      console.error('Error calculating quote:', error);
    } finally {
      setLoadingQuote(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf', 'application/postscript'];
    if (!allowedTypes.includes(file.type) && !file.name.endsWith('.ai')) {
      toast.error('Invalid file type. Please upload PNG, JPG, SVG, AI, or PDF');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setLogoFile(file);
    uploadLogo(file);
  };

  const uploadLogo = async (file) => {
    setUploadingLogo(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/bulk-orders/upload-logo`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, logo_url: response.data.file_url }));
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
      setLogoFile(null);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.quantity < 50) {
      toast.error('Minimum order quantity is 50 units');
      return;
    }

    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        estimated_price: quote?.total_price || null
      };

      const submitRes = await axios.post(`${API_URL}/api/bulk-orders/`, submitData);

      // Meta Pixel — Lead (deduped via server-generated fb_event_id)
      trackLead({
        contentName: `Bulk Order — ${formData.product_type}`,
        value: quote?.total_price || 0,
        eventID: submitRes.data?.fb_event_id,
      });

      toast.success('Bulk order request submitted successfully!');
      
      // Show success message
      setTimeout(() => {
        navigate('/');
      }, 3000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit bulk order');
    } finally {
      setSubmitting(false);
    }
  };

  const pricingTiers = [
    { range: '50-99', discount: '10%', turnaround: '7-10 business days' },
    { range: '100-249', discount: '15%', turnaround: '10-14 business days' },
    { range: '250-499', discount: '20%', turnaround: '14-21 business days' },
    { range: '500+', discount: '25%', turnaround: 'Custom timeline' }
  ];

  const features = [
    { icon: Package, title: 'Free Design Consultation', desc: 'Work with our team to perfect your design' },
    { icon: TrendingDown, title: 'Volume Discounts', desc: 'Save up to 25% on orders of 500+' },
    { icon: Users, title: 'White Glove Service', desc: 'Dedicated account manager for your order' },
    { icon: Zap, title: 'Fast Turnaround', desc: 'Rush orders available for tight deadlines' }
  ];

  const faqs = [
    { q: "What's the minimum order quantity?", a: "50 units for all product types" },
    { q: "How long does production take?", a: "7-21 business days depending on quantity and customization" },
    { q: "Can I get a sample before ordering?", a: "Yes! Free samples for 100+ unit orders, $10 fee for smaller orders" },
    { q: "What file formats do you accept for logos?", a: "PNG, JPG, SVG, AI, and PDF. Minimum 300 DPI or 1000x1000px" },
    { q: "Do you ship internationally?", a: "Yes, we ship worldwide. Contact us for international shipping rates" }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/')}
              className="text-2xl font-bold text-white flex items-center gap-2 hover:text-blue-400 transition-colors"
            >
              <Wifi className="w-7 h-7 text-blue-500" />
              ShopNFCTags
            </button>
            
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => navigate('/products')} className="text-gray-300 hover:text-white transition-colors">
                Shop
              </button>
              <a href="/#how-it-works" className="text-gray-300 hover:text-white transition-colors">
                How It Works
              </a>
              <button onClick={() => navigate('/contact')} className="text-gray-300 hover:text-white transition-colors">
                Contact
              </button>
            </div>
            
            <Button 
              onClick={() => navigate('/products')}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              Shop Now
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 bg-gradient-to-b from-blue-900/20 to-black">
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Bulk Orders & Custom Solutions
          </h1>
          <p className="text-xl text-gray-400 mb-4">
            Special pricing for orders of 50+ units. Perfect for events, corporate gifts, and businesses
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-300">
            <span className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-blue-500" /> Volume Discounts
            </span>
            <span className="flex items-center gap-2">
              <Package className="w-4 h-4 text-blue-500" /> Custom Branding
            </span>
            <span className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-500" /> Dedicated Support
            </span>
          </div>
        </div>
      </section>

      {/* Pricing Tiers Table */}
      <section className="py-12 px-6 bg-gray-900/50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold text-white text-center mb-8">Volume Pricing</h2>
          <div className="grid md:grid-cols-4 gap-4">
            {pricingTiers.map((tier, index) => (
              <Card key={index} className="bg-gray-800 border-gray-700 hover:border-blue-500 transition-colors">
                <CardContent className="p-6 text-center">
                  <div className="text-3xl font-bold text-blue-500 mb-2">{tier.discount}</div>
                  <div className="text-gray-400 text-sm mb-1">OFF</div>
                  <div className="text-white font-semibold mb-3">{tier.range} units</div>
                  <div className="text-xs text-gray-500">{tier.turnaround}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="bg-gray-900 border-gray-800">
                <CardContent className="p-6">
                  <feature.icon className="w-10 h-10 text-blue-500 mb-3" />
                  <h3 className="text-white font-bold mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Main Form */}
      <section className="py-16 px-6 bg-gray-900/30">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl font-bold text-white text-center mb-8">Request Your Custom Quote</h2>
          
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Contact Information */}
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">Contact Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Company Name *</label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-400 mb-2">Job Title / Role</label>
                    <input
                      type="text"
                      name="job_title"
                      value={formData.job_title}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Order Details with Live Quote */}
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">Order Details</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Product Type *</label>
                    <select
                      name="product_type"
                      value={formData.product_type}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="NFC Keychains">NFC Keychains</option>
                      <option value="NFC Business Cards">NFC Business Cards</option>
                      <option value="NFC Signs">NFC Signs</option>
                      <option value="Mixed Order">Mixed Order</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Quantity * (min 50)</label>
                    <input
                      type="number"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      min="50"
                      required
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Live Quote Calculator */}
                {quote && (
                  <div className="mt-6 p-6 bg-gradient-to-r from-blue-900/30 to-blue-800/30 border border-blue-500/30 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-bold text-white flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-blue-500" />
                        Estimated Quote
                      </h4>
                      {loadingQuote && <div className="text-blue-400 text-sm">Calculating...</div>}
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-gray-400">Unit Price</div>
                        <div className="text-white font-bold">${quote.unit_price}</div>
                      </div>
                      <div>
                        <div className="text-gray-400">Discount</div>
                        <div className="text-green-400 font-bold">{quote.discount_percent}% OFF</div>
                      </div>
                      <div>
                        <div className="text-gray-400">Total Savings</div>
                        <div className="text-green-400 font-bold">${quote.savings}</div>
                      </div>
                      <div>
                        <div className="text-gray-400">Total Price</div>
                        <div className="text-blue-400 font-bold text-xl">${quote.total_price}</div>
                      </div>
                    </div>
                    <div className="mt-4 text-xs text-gray-400">
                      *Estimated price. Final quote may vary based on customization requirements.
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Customization */}
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-white">Customization</h3>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="needs_branding"
                      checked={formData.needs_branding}
                      onChange={handleInputChange}
                      className="w-4 h-4 rounded border-gray-700 bg-gray-800 text-blue-500 focus:ring-blue-500"
                    />
                    <span className="text-gray-400 text-sm">I need custom branding</span>
                  </label>
                </div>

                {formData.needs_branding && (
                  <div className="space-y-4 pt-4 border-t border-gray-800">
                    {/* Logo Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Upload Logo</label>
                      <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                        <input
                          type="file"
                          id="logo-upload"
                          accept=".png,.jpg,.jpeg,.svg,.ai,.pdf"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                        <label htmlFor="logo-upload" className="cursor-pointer">
                          <Upload className="w-10 h-10 text-gray-500 mx-auto mb-2" />
                          {uploadingLogo ? (
                            <p className="text-blue-400">Uploading...</p>
                          ) : logoFile ? (
                            <div className="text-green-400 flex items-center justify-center gap-2">
                              <Check className="w-5 h-5" />
                              {logoFile.name}
                            </div>
                          ) : (
                            <>
                              <p className="text-gray-400 mb-1">Click to upload or drag and drop</p>
                              <p className="text-gray-600 text-xs">PNG, JPG, SVG, AI, PDF (max 5MB, 300+ DPI)</p>
                            </>
                          )}
                        </label>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Brand Colors</label>
                      <div className="flex gap-3 items-center">
                        <input
                          type="color"
                          name="brand_colors"
                          value={formData.brand_colors}
                          onChange={handleInputChange}
                          className="w-16 h-10 rounded border border-gray-700 bg-gray-800"
                        />
                        <input
                          type="text"
                          name="brand_colors"
                          value={formData.brand_colors}
                          onChange={handleInputChange}
                          className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Custom Text / Engraving</label>
                      <input
                        type="text"
                        name="custom_text"
                        value={formData.custom_text}
                        onChange={handleInputChange}
                        placeholder="e.g., Company name, tagline"
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Additional Design Notes</label>
                      <textarea
                        name="design_notes"
                        value={formData.design_notes}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Project Details */}
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4">Project Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Intended Use Case *</label>
                    <select
                      name="use_case"
                      value={formData.use_case}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="Corporate Event">Corporate Event</option>
                      <option value="Marketing Campaign">Marketing Campaign</option>
                      <option value="Employee Gifts">Employee Gifts</option>
                      <option value="Resale">Resale</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Target Delivery Date *</label>
                      <input
                        type="date"
                        name="delivery_date"
                        value={formData.delivery_date}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Timeline Flexibility</label>
                      <select
                        name="timeline_flexibility"
                        value={formData.timeline_flexibility}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                      >
                        <option value="flexible">Flexible</option>
                        <option value="fixed">Fixed Deadline</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Shipping Address *</label>
                    <textarea
                      name="shipping_address"
                      value={formData.shipping_address}
                      onChange={handleInputChange}
                      required
                      rows="3"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">Special Requirements</label>
                    <textarea
                      name="special_requirements"
                      value={formData.special_requirements}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Sample Request */}
            <Card className="bg-gray-900 border-gray-800">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    name="request_sample"
                    checked={formData.request_sample}
                    onChange={handleInputChange}
                    className="w-5 h-5 mt-1 rounded border-gray-700 bg-gray-800 text-blue-500 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <label className="block text-white font-semibold mb-1 cursor-pointer">
                      Request a Sample Before Ordering
                    </label>
                    <p className="text-gray-400 text-sm mb-3">
                      {formData.quantity >= 100 
                        ? '✓ Free sample for orders 100+ units' 
                        : '$10 sample fee (deducted from final order)'}
                    </p>
                    {formData.request_sample && (
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-2">Sample Shipping Address</label>
                        <textarea
                          name="sample_address"
                          value={formData.sample_address}
                          onChange={handleInputChange}
                          rows="2"
                          className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex flex-col items-center gap-4">
              <Button
                type="submit"
                disabled={submitting}
                className="bg-blue-500 hover:bg-blue-600 text-white px-12 py-6 text-lg font-semibold"
              >
                {submitting ? 'Submitting...' : 'Request Bulk Quote'}
              </Button>
              <p className="text-gray-400 text-sm text-center">
                You'll receive a custom quote within 24 hours • No commitment required
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-6 bg-gray-900/50">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-3xl font-bold text-white text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-5 gap-4">
            {['Submit Request', 'Get Quote (24hrs)', 'Approve Design', 'Production', 'Delivery'].map((step, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center text-xl font-bold mb-3">
                    {index + 1}
                  </div>
                  <div className="text-white font-semibold text-sm">{step}</div>
                </div>
                {index < 4 && (
                  <div className="hidden md:block absolute top-6 left-1/2 w-full h-0.5 bg-blue-500/30" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6">
        <div className="container mx-auto max-w-3xl">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="bg-gray-900 border-gray-800 hover:border-blue-500 transition-colors">
                <CardContent className="p-6">
                  <h3 className="text-white font-bold mb-2">{faq.q}</h3>
                  <p className="text-gray-400 text-sm">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-gradient-to-r from-blue-600 to-cyan-600">
        <div className="container mx-auto text-center max-w-2xl">
          <h2 className="text-4xl font-bold text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Our team is here to help. Contact us directly for personalized assistance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/contact')}
              className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8 py-6"
            >
              Contact Us
            </Button>
            <a 
              href="mailto:info@shopnfctags.com"
              className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white hover:bg-white/10 font-semibold px-8 py-6 rounded-lg transition-colors"
            >
              info@shopnfctags.com
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-800 py-8">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-500 text-sm">© 2026 ShopNFCTags. All rights reserved.</p>
        </div>
      </footer>
      <ScrollToTop />
    </div>
  );
};

export default BulkOrders;
